import { useTranslation } from "react-i18next";
import { useU2101Context } from "../U2101Context";
import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { doDeleteData, doSearch, luHandleAppiError, type LogicalUnit } from "../U2101Service";

import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { NS, U2101ROLES } from "../U2101";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { U2000IdCheckedBoxDisplay } from "../../u2000Core/components/tables/display/U2000IdCheckedBoxDisplay";
import { U2000IdHeaderCheckedBoxDisplay } from "../../u2000Core/components/tables/display/U2000IdHeaderCheckedBoxDisplay";
import type { U2000TableColumn } from "../../u2000Core/components/tables/basic/U2000BasicTableList";
import React, { useEffect, useRef } from "react";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import { useU2000QuickSearchListEffect } from "../../u2000Core/hooks/useU2000QuickSearchListEffect";
import { useNavigate } from "react-router-dom";
import type { TFunction } from "i18next";
import { u2000BuildQuickSearchFormPath, u2000ShouldQuickSearchOpenForm } from "../../u2000Core/tools/U2000QuickSearchUtils";

import type { U2000TableHandlers } from "../../u2000Core/types/U2000TableHandlers";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextPageAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { U2101LogicalUnitListToolBar } from "./list/U2101LogicalUnitListToolBar";
import { LOGICAL_UNIT_TYPES } from "./common/logicalUnitTypes";
import U2101LogicalUnitDeleteDlg from "./common/U2101LogicalUnitDeleteDlg";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { useU2000ExportExcelHandlers } from "../../u2000Core/hooks/handler/U2000UseExportExcelHandlers";
import { useU2000ExportExcelNotification } from "../../u2000Core/hooks/handler/U2000UseExportExcelNotification";
import { doExportExcel } from "../U2101Service";
import { useU2101FindLogicalUnitCategoryHandlers } from "../U2101UseFindLogicalUnitCategoryHandlers";
import {
  defaultItemToDisplay,
  defaultItemToValue,
  registerCriteriaLookup,
  unregisterCriteriaLookup,
  U2000_CRITERIA_LOOKUP_U2101_FIND_LOGICAL_UNIT_CATEGORY,
} from "../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry";

export function useU2101LogicalUnitListHandlerActions<T>(handlers: U2000TableHandlers<T>): U2000TableHandlers<T> {
  return handlers;
}

function formatCategoryGridLabel(row: any, lang: string): string {
  const cat = row?.logicalUnitCategory;
  if (cat && typeof cat === "object") {
    const code = cat.code ?? row.logicalUnitCategoryCode ?? "";
    const label = lang.startsWith("en") ? cat.labelEn || cat.labelFr : cat.labelFr || cat.labelEn;
    if (code && label) return `${code} - ${label}`;
    return code || label || "";
  }
  const code = row?.logicalUnitCategoryCode ?? "";
  const label = lang.startsWith("en")
    ? row?.logicalUnitCategoryLabelEn || row?.logicalUnitCategoryLabelFr
    : row?.logicalUnitCategoryLabelFr || row?.logicalUnitCategoryLabelEn;
  if (code && label) return `${code} - ${label}`;
  return code || label || "";
}

// Function to get columns definition
function getColumnsDef(t: TFunction, lang: string = "fr"): U2000TableColumn[] {
  return [
    {
      name: "id",
      label: "",
      widthClass: "w-[50px]",
      type: "number",
      align: "center",
      headerDisplay: (column: any, label: string, allRowSelected: boolean, handleSelectAllRow?: (isSelected: boolean) => void) => {
        return <U2000IdHeaderCheckedBoxDisplay column={column} label={label} disabled={false} allRowSelected={allRowSelected} handleSelectAllRow={handleSelectAllRow} />;
      },
      display: (column: any, value: any, row: any, allRowSelected: boolean, rowUpdated?: (row: any) => void) => {
        return <U2000IdCheckedBoxDisplay column={column} value={value} row={row} disabled={false} rowUpdated={rowUpdated} allowRowSelection={allRowSelected} />;
      },
      isCriteriaField: false,
    },
    {
      name: "logicalUnitCode",
      label: t("lblLogicalUnitCode", { ns: NS }),
      widthClass: "w-[200px]",
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      type: "string",
      align: "left",
      inGridField: true,
      sortable: true,
      excludeConditions: ["contains", "endswith"],
      isCriteriaField: true,
    },
    {
      name: "logicalUnitDescription",
      label: t("lblLogicalUnitDescription", { ns: NS }),
      isDefaultColumn: true,
      type: "string",
      inGridField: true,
      sortable: true,
      includeConditions: ["contains", "startswith", "in"],
      isCriteriaField: true,
    },
    {
      name: "logicalUnitCategory",
      label: t("lblLogicalUnitCategory", { ns: NS }),
      widthClass: "w-[220px]",
      isDefaultColumn: true,
      type: "string",
      align: "left",
      inGridField: true,
      sortable: false,
      isCriteriaField: true,
      includeConditions: ["equals", "in"],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U2101_FIND_LOGICAL_UNIT_CATEGORY,
        valueKey: "id",
        criteriaField: "logicalUnitCategory.id",
        searchCriterionField: "code",
        displayKeys: ["code", lang.startsWith("en") ? "labelEn" : "labelFr"],
        placeholder: t("phCriteriaCategory", { ns: "common" }),
      },
      display: (_column: any, _value: any, row: any) => {
        const text = formatCategoryGridLabel(row, lang);
        return text || "";
      },
    },
    {
      name: "logicalUnitTypes",
      label: t("lblLogicalUnitTypes", { ns: NS }),
      isDefaultColumn: true,
      type: "string",
      inGridField: true,
      sortable: true,
      align: "left",
      display: (_column: any, value: any, _row: any) => {
        // value peut être un tableau de valeurs (ex: ["admin", "organizational"])
        const getLabel = (val: string) => {
          const found = LOGICAL_UNIT_TYPES.find((type: any) => type.value === val);
          if (!found) return val;
          if (typeof found.label === "object" && found.label !== null) {
            const labels = found.label as unknown as Record<string, string>;
            return labels[lang] ?? labels["fr"] ?? Object.values(labels)[0] ?? val;
          }
          return String(found.label);
        };
        if (Array.isArray(value)) {
          return (
            <div className="flex h-full items-center justify-start gap-1">
              {value.map((val: string, idx: number) => (
                <span key={idx} className={idx % 2 === 0 ? "rounded-sm bg-blue-100 px-1 text-blue-800" : "rounded-sm bg-red-100 px-1 text-red-800"}>
                  {getLabel(val)}
                </span>
              ))}
            </div>
          );
        }
        return <span className="rounded-sm bg-blue-100 px-1 text-blue-800">{getLabel(value)}</span>;
      },
      isCriteriaField: true,
    },
    {
      name: "deletedAt",
      label: t("lblCndFieldDeletedAt", { ns: NS }),
      widthClass: "w-[400px]",
      type: "date",
      inGridField: true,
      align: "center",
      display: (value) => {
        return value ? <span className="font-bold text-red-600">DELETED</span> : "";
      },
      includeConditions: ["isNotNull"],
      ovverideLabelConditions: [
        {
          name: "isNotNull",
          label: t("lblCndDeletedAt", { ns: NS }),
        },
      ],
      isCriteriaField: true,
    },
    {
      name: "createdAt",
      label: t("lblFldCreatedAt", { ns: NS }),
      widthClass: "w-[150px]",
      isDefaultColumn: true,
      type: "date",
      inGridField: true,
      sortable: true,
      includeConditions: ["equals", "notequals", "greaterthan", "greaterthanorequal", "lesthan", "lesthanorequal", "interval"],
      isCriteriaField: true,
    },
  ];
}

const U2101LogicalUnitList: React.FC = () => {
  // Navigation
  const navigate = useNavigate();

  // Translation
  const { t, i18n } = useTranslation();

  // Toast
  const toast = useToast();

  // Auth context for role checking
  const { hasRole } = useU2000AuthContext();

  // Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU2101Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb, quickSearchData, setQuickSearchData } = useU2000LogicalUnitContext();
  const consumeQsSessionRef = useRef<() => { isFromQuickSearch: boolean; targetLogicalUnit: string } | null>(() => null);
  const findLogicalUnitCategoryHandlers = useU2101FindLogicalUnitCategoryHandlers();

  useEffect(() => {
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U2101_FIND_LOGICAL_UNIT_CATEGORY, {
      search: findLogicalUnitCategoryHandlers.search,
      itemToValue: defaultItemToValue,
      itemToDisplay: defaultItemToDisplay,
    });
    return () => unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U2101_FIND_LOGICAL_UNIT_CATEGORY);
  }, [findLogicalUnitCategoryHandlers.search]);

  // Initialize columns on mount or when 't' changes
  useEffect(() => {
    const columns = getColumnsDef(t, i18n.language);
    searchStateHandlers.setColumns(columns);
    gridStateHandlers.setColumns(columns);
    setLogicalUnitBreadcrumb([{ label: t("title", { ns: NS }) }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, i18n.language]);

  // Manage Handler Actions
  const u2101LogicalUnitListHandlerActions: U2000TableHandlers<LogicalUnit> = useU2101LogicalUnitListHandlerActions<LogicalUnit>({
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (logicalUnit: LogicalUnit) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom("");
      const currentIdx = gridStateHandlers ? gridStateHandlers.getData().findIndex((r) => r.id === logicalUnit.id) : -1;
      if (gridStateHandlers) {
        gridStateHandlers.setCurrentIdx(currentIdx);
      }
      navigate(`${logicalUnit.id}`);
    },
    onAdd: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom("/U2101");
      navigate("new");
    },
    onModify: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom("/U2101");
      navigate(`${gridStateHandlers.getSelectedRows()[0]?.id}`);
    },
    onDelete: () => {
      handleDelete.handleDelete();
    },
    onExportExcel: async () => {
      const filter = searchStateHandlers.extractSearchParameter();
      await handleExportExcel.handleExportExcel(filter);
    },
  });

  // Call Search Handlers for Logical Unit
  const handleSearch = useU2000SearchHandlers<LogicalUnit>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      return await doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<LogicalUnit>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());

      const qsSession = consumeQsSessionRef.current();
      const isFromQuickSearch = qsSession?.isFromQuickSearch === true;
      const targetLU = qsSession?.targetLogicalUnit ?? "/U2101";
      if (
        u2000ShouldQuickSearchOpenForm(window.location.pathname, targetLU) &&
        searchResult.data &&
        searchResult.data.length === 1 &&
        isFromQuickSearch
      ) {
        const singleLU = searchResult.data[0];
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        luStateHandlers.setActionOriginFrom("");
        const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === singleLU.id);
        if (currentIdx !== -1) {
          gridStateHandlers.setCurrentIdx(currentIdx);
        }
        navigate(u2000BuildQuickSearchFormPath(targetLU, singleLU.id), { replace: true });
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Export Excel Handlers for Logical Unit
  const handleExportExcel = useU2000ExportExcelHandlers({
    doExportExcelFn: async (filter, lang) => {
      return await doExportExcel(u2000AxiosAuthenticated, filter, lang);
    },
    i18nLanguage: i18n.language,
    onSuccess: (processId: string) => {
      toast.success(
        t("msgExportExcelStarted", { processId, ns: "common" }) || `Export en cours (référence: ${processId})`,
        t("successTitle", { ns: "common" })
      );
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Écouter les notifications WebSocket pour les exports Excel terminés
  useU2000ExportExcelNotification();

  const { consumeQuickSearchSession } = useU2000QuickSearchListEffect({
    targetLogicalUnit: "/U2101",
    t,
    searchStateHandlers,
    gridStateHandlers,
    runSearch: handleSearch.handleSearch,
    quickSearchData,
    setQuickSearchData,
  });
  consumeQsSessionRef.current = consumeQuickSearchSession;

  // Gestion des Delete Record avec confirmation
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => (gridStateHandlers ? gridStateHandlers.getSelectedRows() : []),
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, row);
    },

    onSuccess: (deletedRows) => {
      const page = gridStateHandlers.getCurrentPage();
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row["logicalUnitCode"], ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      // Après suppression, aller au prochain enregistrement, aller vers l'avant si possible, sinon vers l'arrière, si aucun, retourner à la liste
      const action: U2000GridStateNextPageAfterDeleteReturn = gridStateHandlers.getNextPageAfterDelete(page, totalRows);
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // s'assurer que les colonnes sont chargées avant le rendu car creer des problemes d'affichage avec les columnes par defaut si searchStateHandlers.columns est vide au rendu
  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  // Render
  return (
    <>
      <U2101LogicalUnitDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />

      <U2000BasicTable
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        actionsButtons={<U2101LogicalUnitListToolBar gridStateHandlers={gridStateHandlers as any} handlerActions={u2101LogicalUnitListHandlerActions as any} />}
        handlerActions={u2101LogicalUnitListHandlerActions}
        label={t("lblLogicalUnit", {
          count: gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
        allowRowSelection={true}
        exportExcelTitle={t("btnExportExcel", { ns: "common" })}
        isExporting={handleExportExcel.isExporting}
        hasExportRole={hasRole(U2101ROLES.EXPORT)}
      />
    </>
  );
};

export default U2101LogicalUnitList;
